import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  EmptyState,
  Icon,
  Image,
  PageDots,
  Text,
  Video,
  type Color,
} from "@salestrak/ui";
import {
  ANNOUNCEMENT_TYPE_LABELS,
  announcementDate,
  isEarlyAccess,
  type Announcement,
} from "@salestrak/announcements";
import { MarkdownContent } from "#app/components/navigation/markdown-content";
import { ANNOUNCEMENT_CONTENT } from "#app/components/navigation/whats-new-content";
import { APP_VERSION } from "#app/lib/app-version";
import { CHANGELOG_URL } from "#app/lib/marketing-urls";
import {
  formatMonthYearUtc,
  relativeDayUtc,
} from "#app/lib/utils/format-date";

// The entry's kind, as the one word that leads its group heading and its list row.
// "New" reads informational, shipped work reads as success, early access
// and the fix roundups stay on the muted rung — a chip would shout on every
// row, the word carries the hue on its own.
function entryKind(entry: Announcement): { label: string; color: Color } {
  if (isEarlyAccess(entry)) return { label: "Early access", color: "muted" };
  if (entry.type === "new") return { label: "New", color: "info" };
  if (entry.type === "improved") return { label: "Improved", color: "success" };
  return { label: ANNOUNCEMENT_TYPE_LABELS[entry.type], color: "muted" };
}

/**
 * The curated What's-New feed in two states. SPOTLIGHT: one unseen entry per
 * screen — media or the entry's registry vignette on top, then kind · date,
 * title, body and the one primary action — stepped by a pager. The entries
 * the viewer already saw wait behind "N earlier updates" in the pager bar.
 * LIST: those earlier entries as compact rows grouped by month, each opening
 * in place; the dialog opens straight here when nothing is unseen. `unseenKeys`
 * decides the split; the caller marks everything seen when the dialog closes,
 * so the split holds the whole time it is open.
 */
export function WhatsNewDialog({
  open,
  onOpenChange,
  entries,
  unseenKeys,
  onNavigate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: Announcement[];
  unseenKeys: ReadonlySet<string>;
  onNavigate: (route: string, entryKey: string) => void;
}) {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      {/* The popup unmounts on close, so the body's page/list state resets
          with it — every open starts on the first unseen entry. */}
      <DialogPopup>
        <WhatsNewBody
          entries={entries}
          onClose={() => onOpenChange(false)}
          onNavigate={onNavigate}
          unseenKeys={unseenKeys}
        />
      </DialogPopup>
    </Dialog>
  );
}

function WhatsNewBody({
  entries,
  unseenKeys,
  onNavigate,
  onClose,
}: {
  entries: Announcement[];
  unseenKeys: ReadonlySet<string>;
  onNavigate: (route: string, entryKey: string) => void;
  onClose: () => void;
}) {
  const spotlight = entries.filter((entry) => unseenKeys.has(entry.key));
  const earlier = entries.filter((entry) => !unseenKeys.has(entry.key));
  const [index, setIndex] = React.useState(0);
  const [listOpen, setListOpen] = React.useState(false);

  if (spotlight.length > 0 && !listOpen) {
    const current = spotlight[Math.min(index, spotlight.length - 1)]!;
    const last = index >= spotlight.length - 1;
    return (
      <>
        <SpotlightEntry entry={current} onNavigate={onNavigate} />
        <Box
          align="center"
          border="top"
          direction="row"
          gap="sm"
          paddingBottom="md"
          paddingTop="base"
          paddingX="md"
        >
          <Box direction="row" flex={1}>
            {earlier.length > 0 && (
              <Button
                onClick={() => setListOpen(true)}
                size="sm"
                variant="ghost"
              >
                {earlier.length} earlier {earlier.length === 1 ? "update" : "updates"}
                <Icon name="chevron-down" size="xs" />
              </Button>
            )}
          </Box>
          <PageDots
            count={spotlight.length}
            index={index}
            label="New updates"
            onIndexChange={setIndex}
          />
          <Box direction="row" flex={1} gap="xs" justify="end">
            {index > 0 && (
              <Button
                onClick={() => setIndex(index - 1)}
                size="sm"
                variant="ghost"
              >
                Back
              </Button>
            )}
            {last ? (
              <Button onClick={onClose} size="sm" variant="outline">
                Done
              </Button>
            ) : (
              <Button
                onClick={() => setIndex(index + 1)}
                size="sm"
                variant="outline"
              >
                Next
                <Icon name="arrow-right" size="xs" />
              </Button>
            )}
          </Box>
        </Box>
      </>
    );
  }

  const rows = spotlight.length > 0 ? earlier : entries;
  return (
    <>
      <DialogHeader>
        <Box align="center" direction="row" gap="sm">
          {spotlight.length > 0 ? (
            <Button
              aria-label="Back to new updates"
              onClick={() => setListOpen(false)}
              size="icon-xs"
              variant="ghost"
            >
              <Icon name="chevron-up" />
            </Button>
          ) : (
            <Icon color="primary" name="gift" size="lg" />
          )}
          <DialogTitle>
            {spotlight.length > 0 ? "Earlier updates" : "What's new"}
          </DialogTitle>
        </Box>
      </DialogHeader>
      <DialogPanel>
        {rows.length === 0 ? (
          <Box paddingY="lg">
            <EmptyState
              size="xs"
              title="Nothing new right now — check back soon."
            />
          </Box>
        ) : (
          <EntryList entries={rows} onNavigate={onNavigate} />
        )}
      </DialogPanel>
      {/* Separated by a rule, not a fill — the dialog is one surface. */}
      <Box
        align="center"
        border="top"
        direction="row"
        gap="xs"
        paddingX="lg"
        paddingY="sm"
      >
        <Text mono size="2xs" variant="faint">
          v{APP_VERSION}
        </Text>
        <Box grow />
        <ChangelogLink />
      </Box>
    </>
  );
}

/** The quiet way out to the public history — a footnote, never a button. */
function ChangelogLink() {
  return (
    <Text
      as="a"
      href={CHANGELOG_URL}
      rel="noopener noreferrer"
      size="xs"
      target="_blank"
      underline="hover"
      variant="muted"
    >
      <Box align="center" direction="row" gap="2xs">
        Full changelog
        <Icon color="muted" name="external-link" size="xs" />
      </Box>
    </Text>
  );
}

function SpotlightEntry({
  entry,
  onNavigate,
}: {
  entry: Announcement;
  onNavigate: (route: string, entryKey: string) => void;
}) {
  const kind = entryKind(entry);
  const RichContent = ANNOUNCEMENT_CONTENT[entry.key];
  const hasHero = entry.media != null || RichContent != null;
  return (
    <>
      {hasHero && (
        <Box
          align="center"
          background="sunken"
          border="bottom"
          direction="row"
          height={{ base: 200, sm: 232 }}
          justify="center"
          overflow="hidden"
          padding="md"
          surface="sunken"
        >
          {entry.media ? (
            <EntryMedia media={entry.media} />
          ) : RichContent ? (
            <Box fill="width" maxWidth={320}>
              <RichContent />
            </Box>
          ) : null}
        </Box>
      )}
      <Box direction="column" gap="lg" padding="lg" paddingTop={hasHero ? "lg" : "xl"}>
        <Box direction="column" gap="sm">
          <Box align="center" direction="row" gap="xs">
            <Text color={kind.color} size="xs" weight="semibold">
              {kind.label}
            </Text>
            <Text size="xs" variant="faint">
              ·
            </Text>
            <Text size="xs" variant="muted">
              {relativeDayUtc(announcementDate(entry))}
            </Text>
          </Box>
          <DialogTitle>{entry.title}</DialogTitle>
          <MarkdownContent markdown={entry.body} />
        </Box>
        <Box align="center" direction="row" gap="sm">
          {entry.cta && (
            <Button onClick={() => onNavigate(entry.cta!.route, entry.key)}>
              {entry.cta.label}
              <Icon name="arrow-right" size="xs" />
            </Button>
          )}
          <Box grow />
          <ChangelogLink />
        </Box>
      </Box>
    </>
  );
}

function EntryMedia({ media }: { media: NonNullable<Announcement["media"]> }) {
  if (media.kind === "video") {
    return (
      <Box overflow="hidden" rounded="md">
        <Video
          aria-label={media.alt}
          fill="width"
          poster={media.poster}
          src={media.src}
        />
      </Box>
    );
  }
  return (
    <Box fill="size" overflow="hidden" rounded="md">
      <Image alt={media.alt} fill fit="cover" src={media.src} />
    </Box>
  );
}

/** Compact rows grouped by month, each opening in place. */
function EntryList({
  entries,
  onNavigate,
}: {
  entries: Announcement[];
  onNavigate: (route: string, entryKey: string) => void;
}) {
  const groups: Array<{ month: string; entries: Announcement[] }> = [];
  for (const entry of entries) {
    const month = formatMonthYearUtc(announcementDate(entry));
    const group = groups[groups.length - 1];
    if (group && group.month === month) group.entries.push(entry);
    else groups.push({ month, entries: [entry] });
  }
  return (
    <Box direction="column" gap="md">
      {groups.map((group) => (
        <Box key={group.month} direction="column" gap="2xs">
          <Text size="xs" variant="muted" weight="semibold">
            {group.month}
          </Text>
          <Accordion>
            {group.entries.map((entry) => (
              <EntryRow key={entry.key} entry={entry} onNavigate={onNavigate} />
            ))}
          </Accordion>
        </Box>
      ))}
    </Box>
  );
}

function EntryRow({
  entry,
  onNavigate,
}: {
  entry: Announcement;
  onNavigate: (route: string, entryKey: string) => void;
}) {
  const kind = entryKind(entry);
  const RichContent = ANNOUNCEMENT_CONTENT[entry.key];
  return (
    <AccordionItem separator={false} value={entry.key}>
      <AccordionTrigger size="sm">
        {/* Titles lead and line up; the kind rides the date so a long kind
            ("Early access") never pushes its title out of the column. */}
        <Box align="center" direction="row" gap="sm" grow minWidth={0}>
          <Text size="sm" truncate={1} weight="medium">
            {entry.title}
          </Text>
          <Box grow />
          <Box align="center" direction="row" gap="xs" shrink={false}>
            <Text color={kind.color} size="xs" weight="semibold">
              {kind.label}
            </Text>
            <Text size="xs" tabular variant="muted">
              {relativeDayUtc(announcementDate(entry))}
            </Text>
          </Box>
        </Box>
      </AccordionTrigger>
      <AccordionPanel>
        <Box direction="column" gap="sm">
          <MarkdownContent markdown={entry.body} />
          {entry.media && <EntryMedia media={entry.media} />}
          {RichContent && <RichContent />}
          {entry.cta && (
            <Box direction="row">
              <Button
                onClick={() => onNavigate(entry.cta!.route, entry.key)}
                size="sm"
                variant="outline"
              >
                {entry.cta.label}
                <Icon name="arrow-right" size="xs" />
              </Button>
            </Box>
          )}
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );
}
